/**
 * Passage-highlight helpers for every surface that lets a reader annotate
 * passage text (module capture + review regeneration). Adapted from the SHSAT
 * Adaptive version
 * (``student/src/pages/private/DigitalSHSAT/AdaptiveModule/passageHighlight.ts``)
 * with one addition: rendered KaTeX constructs are treated as ATOMIC
 * highlight units.
 *
 * DUPLICATED BYTE-FOR-BYTE in ``student/src/global/``, ``client/src/global/``
 * and ``tutor/src/global/``. The three frontends are separate webpack builds
 * with no shared package (same pattern as ``essays.ts``), so keep the copies
 * identical — ``diff`` between them is the only drift detector there is.
 *
 * Why atomic: KaTeX renders a construct's glyphs into separate nested spans,
 * so a construct's ``textContent`` is never a contiguous substring of its own
 * markup. An accent like ``\'i`` yields ``"iˊ"`` (base letter + modifier
 * span); ``\text{---}`` yields a single ``—`` glyph; ``08279+5255`` is split
 * across two ``.base`` spans at the binary operator. The legacy
 * ``innerHTML.indexOf(textContent)`` splice therefore returned ``-1`` or a
 * plausible-but-wrong offset, cut the markup mid-attribute, and produced
 * corrupted overlay HTML (duplicated text and raw markup rendering as visible
 * characters). Here a highlight either covers a whole ``.katex`` construct or
 * doesn't touch it, and construct markup is copied verbatim, never spliced.
 *
 * Offset semantics: flat character offsets into the passage element's
 * ``textContent``, identical to what the legacy walk produced for plain
 * text, so previously saved annotations keep their meaning.
 *
 * PRECONDITION: both functions must run after ``renderKatex`` has stripped
 * the hidden ``.katex-mathml`` blocks (it always has by the time a reader
 * can select text), so ``textContent`` lengths only count visible text.
 */

export interface PassageRange {
    startIndex: number;
    endIndex: number;
}

/** Block-level ``\htmlClass`` wrappers used by passage authors to lay out note
 * lists — see the ``.katex-html .indentN / .bullet`` rules in
 * ``global/styles.css``, which set ``display: block`` / ``list-item``. */
const BLOCK_HTML_CLASSES = '.indent1, .indent2, .bullet';

/** A ``.katex`` root that is really a TEXT CONTAINER, not a math expression:
 * the whole passage authored as one ``$\text{\htmlClass{indent2}{...}}...$``
 * block, as "student has taken the following notes" stems are.
 *
 * These must NOT be atomic. Two things go wrong if they are:
 *
 * 1. The construct is the entire note list, so snapping outward turns any
 *    selection into a highlight over every bullet.
 * 2. ``<mark>`` ends up wrapping content that is entirely ``display: block``.
 *    An inline box paints its background over its own line-box fragments, and
 *    a mark whose content is all block-level generates none — so **nothing is
 *    painted at all** and the highlight silently disappears. (Its
 *    ``getBoundingClientRect()`` still reports the union of its children, so
 *    the element looks fine when inspected.)
 *
 * Detected by class name rather than computed ``display`` on purpose: KaTeX
 * lays out ``\frac`` with ``display: inline-table`` / ``table-row``, so a
 * "not inline?" probe would misread every fraction as a text container. */
const isTextContainer = (element: Element): boolean => (
    element.querySelector(BLOCK_HTML_CLASSES) !== null
);

/** A rendered KaTeX construct that must be treated atomically:
 *
 * - a ``.katex`` root that is a real math expression. The passage element
 *   itself also carries the ``katex`` class (it's the auto-render target), so
 *   roots are detected strictly below the passage.
 * - an ``.accent`` wrapper anywhere. Accents need calling out separately
 *   because inside a text container (which is *not* atomic, see above) they
 *   would otherwise be reachable: ``\'i`` renders as a base ``i`` plus a
 *   modifier ``ˊ`` in a sibling span, two characters of ``textContent`` for
 *   one visible glyph, so a boundary landing between them marks half a
 *   character. */
const isKatexRoot = (node: Node): boolean => {
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    const element = node as HTMLElement;
    if (element.classList.contains('accent')) return true;
    return element.classList.contains('katex') && !isTextContainer(element);
};

/** Flat textContent offset of a selection boundary within the passage. */
const flatOffset = (passage: Element, node: Node, offsetInNode: number): number => {
    const range = document.createRange();
    range.setStart(passage, 0);
    range.setEnd(node, offsetInNode);
    return range.toString().length;
};

/** Expand [start, end) outward so no boundary lands strictly inside a
 * rendered KaTeX construct. */
const snapToKatexBoundaries = (
    passage: Element,
    start: number,
    end: number
): PassageRange => {
    let startIndex = start;
    let endIndex = end;
    let flatIndex = 0;

    const walk = (node: Node) => {
        if (isKatexRoot(node)) {
            const length = (node.textContent ?? '').length;
            if (startIndex > flatIndex && startIndex < flatIndex + length) {
                startIndex = flatIndex;
            }
            if (endIndex > flatIndex && endIndex < flatIndex + length) {
                endIndex = flatIndex + length;
            }
            flatIndex += length;
        }
        else if (node.nodeType === Node.TEXT_NODE) {
            flatIndex += (node.textContent ?? '').length;
        }
        else {
            node.childNodes.forEach(walk);
        }
    };

    passage.childNodes.forEach(walk);

    return { startIndex, endIndex };
};

/** Convert the current selection into flat character offsets within the
 * passage, snapping boundaries outward around KaTeX constructs. Returns
 * null for an empty/collapsed selection or one that escapes the passage. */
export const measurePassageSelectionOffsets = (
    passage: Element,
    selection: Selection
): PassageRange | null => {
    const { anchorNode, anchorOffset, focusNode, focusOffset } = selection;

    if (!anchorNode || !focusNode) return null;
    if (!passage.contains(anchorNode) || !passage.contains(focusNode)) return null;

    let start = flatOffset(passage, anchorNode, anchorOffset);
    let end = flatOffset(passage, focusNode, focusOffset);

    if (start > end) [start, end] = [end, start];
    if (start === end) return null;

    return snapToKatexBoundaries(passage, start, end);
};

const escapeText = (text: string): string => (
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
);

/** Build the highlight-overlay HTML: a copy of the passage's markup with
 * the substring [startIndex, endIndex) wrapped in ``<mark>`` tags. Marks
 * open and close per text node, so they never span element boundaries.
 * KaTeX construct roots are leaves: copied verbatim, and wrapped whole in
 * ``<mark>`` when the range overlaps them at all (all-or-nothing, which
 * also tolerates old saved indexes that straddle a construct). */
export const generatePassageHTML = (
    passage: Element,
    startIndex: number,
    endIndex: number
): { passageHTML: string; passagePreview: string } => {
    let passageHTML = '';
    let passagePreview = '';
    let lastIndex = 0;

    const emitText = (textContent: string) => {
        const length = textContent.length;
        const from = Math.max(startIndex - lastIndex, 0);
        const to = Math.min(endIndex - lastIndex, length);

        if (from < length && to > 0 && from < to) {
            passageHTML += escapeText(textContent.slice(0, from));
            passageHTML += '<mark>';
            passageHTML += escapeText(textContent.slice(from, to));
            passageHTML += '</mark>';
            passageHTML += escapeText(textContent.slice(to));
            passagePreview += textContent.slice(from, to);
        }
        else {
            passageHTML += escapeText(textContent);
        }

        lastIndex += length;
    };

    const walk = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            emitText(node.textContent ?? '');
            return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const element = node as HTMLElement;
        const { outerHTML, innerHTML } = element;

        if (isKatexRoot(element)) {
            const textContent = element.textContent ?? '';
            const length = textContent.length;
            const overlaps = startIndex < lastIndex + length && endIndex > lastIndex;

            if (overlaps) {
                passageHTML += `<mark>${outerHTML}</mark>`;
                passagePreview += textContent;
            }
            else {
                passageHTML += outerHTML;
            }

            lastIndex += length;
            return;
        }

        if (!element.hasChildNodes()) {
            // Void or empty elements (img, br, empty spans) have no
            // closing-tag arithmetic to do and no text to offset.
            passageHTML += outerHTML;
            return;
        }

        const openTagEnd = outerHTML.indexOf('>') + 1;
        passageHTML += outerHTML.slice(0, openTagEnd);
        element.childNodes.forEach(walk);
        passageHTML += outerHTML.slice(openTagEnd + innerHTML.length);
    };

    passage.childNodes.forEach(walk);

    return { passageHTML, passagePreview };
};
